<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { Capacitor } from '@capacitor/core';
import { App } from '@capacitor/app';
import { useRouter } from 'vue-router';
import { AppRoute } from '@/libs/routing';
import { version } from '@/version';
import { useGroupStore } from '@/stores/group';
import { useFavoriteGroupsStore } from '@/stores/favorite-groups';

import FavoriteGroupsSheet from '@/components/layout/FavoriteGroupsSheet.vue';
import BaseIcon from '@/components/ui/BaseIcon.vue';
import EmptyState from '@/components/ui/EmptyState.vue';
import NetworkBadge from '@/components/ui/NetworkBadge.vue';
import SettingsHeader from './SettingsHeader.vue';
import SettingsRow from './SettingsRow.vue';

const groupStore = useGroupStore();
const favoriteGroupsStore = useFavoriteGroupsStore();
const router = useRouter();

const appVersion = ref<string>('');
const isNetworksSheetOpen = ref(false);

const hasContact = computed<boolean>(() => {
  const contact = groupStore.group?.contact;
  return Boolean(contact?.email || contact?.phone_number);
});

onMounted(async () => {
  if (Capacitor.getPlatform() === 'ios') {
    const appInfo = await App.getInfo();
    appVersion.value = appInfo.version;
  } else {
    appVersion.value = version;
  }
});
</script>

<template>
  <div class="page page--no-navbar">
    <div class="content settings-page">
      <SettingsHeader :title="$t('settings.title')" />

      <section class="settings-page__section">
        <h2 class="settings-page__section-title">{{ $t('favoriteGroups.title') }}</h2>
        <button
          v-if="favoriteGroupsStore.favorites.length > 0"
          class="settings-page__networks"
          type="button"
          @click="isNetworksSheetOpen = true"
        >
          <span class="settings-page__network-list">
            <span
              v-for="favorite in favoriteGroupsStore.favorites"
              :key="favorite.groupId"
              class="settings-page__network"
            >
              <NetworkBadge :name="favorite.name" :color="favorite.color" :logo-url="favorite.logoUrl" />
              <span class="settings-page__network-name">{{ favorite.name }}</span>
              <BaseIcon
                v-if="favorite.groupId === groupStore.group?.group_id"
                name="check"
                class="settings-page__network-check"
              />
            </span>
          </span>
          <BaseIcon name="chevron-right" class="settings-page__network-chevron" />
        </button>
        <div v-else class="settings-page__card">
          <EmptyState :title="$t('favoriteGroups.empty')">
            <template #actions>
              <button
                class="settings-page__add-network"
                type="button"
                @click="router.push({ name: AppRoute.SELECT_GROUP })"
              >
                {{ $t('favoriteGroups.add') }}
              </button>
            </template>
          </EmptyState>
        </div>
        <FavoriteGroupsSheet v-model:open="isNetworksSheetOpen" />
      </section>

      <section class="settings-page__section">
        <h2 class="settings-page__section-title">{{ $t('settings.title') }}</h2>
        <div class="settings-page__card">
          <SettingsRow
            icon="languages"
            :label="$t('profile.language')"
            trailing-icon="chevron-right"
            @click="router.push({ name: AppRoute.SETTINGS_LANGUAGES })"
          />
          <SettingsRow
            v-if="hasContact"
            icon="headset"
            :label="$t('profile.contactNetwork')"
            trailing-icon="chevron-right"
            @click="router.push({ name: AppRoute.SETTINGS_CONTACT })"
          />
          <SettingsRow
            href="https://web.pysae.com/politique-de-confidentialite"
            icon="file-text"
            :label="$t('profile.confidentialityPolicy')"
            trailing-icon="arrow-up-right"
          />
        </div>
      </section>

      <div v-if="appVersion" class="settings-page__version">{{ appVersion }}</div>
    </div>
  </div>
</template>

<style lang="scss">
.settings-page {
  background-color: $color-neutral-lower;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;

  &__section {
    padding: $spacing-5;
  }

  &__section-title {
    color: $color-neutral-highest;
    font: $heading-h4;
    letter-spacing: $heading-h4-spacing;
    margin: 0 0 $spacing-3;
  }

  &__networks {
    align-items: center;
    background-color: $color-white;
    border: 1px solid $color-border-medium;
    border-radius: $radius-xl;
    cursor: pointer;
    display: flex;
    gap: $spacing-3;
    padding: $spacing-2 $spacing-4;
    text-align: left;
    width: 100%;

    &:active {
      background-color: $color-neutral-low;
    }
  }

  &__network-list {
    display: flex;
    flex: 1;
    flex-direction: column;
    min-width: 0;
  }

  &__network {
    align-items: center;
    display: flex;
    gap: $spacing-3;
    padding: $spacing-2 0;
  }

  &__network-name {
    color: $color-neutral-highest;
    flex: 1;
    font: $label-medium;
    letter-spacing: $label-medium-spacing;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__network-check {
    color: $color-primary;
    flex-shrink: 0;
    font-size: 24px;
  }

  &__network-chevron {
    flex-shrink: 0;
    font-size: 24px;
  }

  &__add-network {
    background-color: $color-neutral-highest;
    border: none;
    border-radius: $radius-pill;
    color: $color-white;
    cursor: pointer;
    font: $label-medium;
    letter-spacing: $label-medium-spacing;
    padding: $spacing-3 $spacing-5;
  }

  &__card {
    background-color: $color-white;
    border-radius: $radius-xl;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    border: 1px solid $color-border-medium;
  }

  &__version {
    color: $color-neutral-higher;
    font: $text-small;
    letter-spacing: $text-small-spacing;
    margin-top: auto;
    padding: $spacing-4 $spacing-5;
    text-align: right;
  }
}
</style>
